import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { baseUrl } from '../../../constants';

@Injectable({ providedIn: 'root' })
export class RiderService {
  private baseUrl = `${baseUrl}Riders`;

  constructor(private http: HttpClient) {}

  /** ✅ Include Authorization Header */
  private get headers() {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('%c[RiderService] ⚠️ No token found in localStorage.', 'color: orange');
    } else {
      console.log('%c[RiderService] 🟢 Using token:', 'color: green', token);
    }

    return {
      headers: new HttpHeaders({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      })
    };
  }

  /** ✅ Get all riders */
  getRiders(): Observable<any[]> {
    console.log('%c[RiderService] 📦 Fetching all riders...', 'color: blue');
    return this.http.get<any[]>(this.baseUrl, this.headers).pipe(
      tap({
        next: (res) => console.log('%c[RiderService] ✅ Riders fetched successfully:', 'color: green', res),
        error: (err) => console.error('[RiderService] ❌ Failed to fetch riders:', err)
      })
    );
  }

  /** ✅ Get rider by ID */
  getRiderById(id: number): Observable<any> {
    console.log('%c[RiderService] 🔍 Fetching rider by ID:', 'color: blue', id);
    return this.http.get<any>(`${this.baseUrl}/${id}`, this.headers).pipe(
      tap({
        next: (res) => console.log('%c[RiderService] ✅ Rider fetched successfully:', 'color: green', res),
        error: (err) => console.error('[RiderService] ❌ Failed to fetch rider:', err)
      })
    );
  }

  /** ✅ Create a new rider */
  createRider(rider: any): Observable<any> {
    console.log('%c[RiderService] ➕ Creating new rider...', 'color: purple', rider);

    return this.http.post(`${this.baseUrl}`, rider, {
      ...this.headers,
      observe: 'response',
      responseType: 'text'
    } as any).pipe(
      tap({
        next: (res) => console.log('%c[RiderService] ✅ Rider created successfully:', 'color: green', res),
        error: (err) => console.error('[RiderService] ❌ Rider creation failed:', err)
      })
    );
  }

  /** ✅ Update an existing rider */
  updateRider(id: number, rider: any): Observable<any> {
    console.log('%c[RiderService] ✏️ Updating rider...', 'color: dodgerblue', { id, rider });

    return this.http.put(`${this.baseUrl}/${id}`, rider, {
      ...this.headers,
      observe: 'response',
      responseType: 'text'
    } as any).pipe(
      tap({
        next: (res) => console.log('%c[RiderService] ✅ Rider updated successfully:', 'color: green', res),
        error: (err) => console.error('[RiderService] ❌ Rider update failed:', err)
      })
    );
  }

  /** ✅ Delete rider */
  deleteRider(id: number): Observable<any> {
    console.log('%c[RiderService] 🗑️ Deleting rider...', 'color: red', id);

    return this.http.delete(`${this.baseUrl}/${id}`, {
      ...this.headers,
      observe: 'response',
      responseType: 'text'
    } as any).pipe(
      tap({
        next: (res) => console.log('%c[RiderService] ✅ Rider deleted successfully:', 'color: green', res),
        error: (err) => console.error('[RiderService] ❌ Rider deletion failed:', err)
      })
    );
  }

  getPhoneAnalytics(workDate?: string): Observable<any> {
    const query = workDate ? `?workDate=${encodeURIComponent(workDate)}` : '';
    return this.http.get<any>(`${this.baseUrl}/phone-analytics${query}`, this.headers);
  }

  openPhoneWork(riderId: number, payload: { phoneState: string; creditAmount: number; workDate?: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/${riderId}/phone-work-open`, payload, this.headers);
  }

  closePhoneWork(riderId: number, payload: { creditAmount: number; workDate?: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/${riderId}/phone-work-close`, payload, this.headers);
  }

  editPhoneLog(
    phoneLogId: number,
    payload: { startPhoneState?: string; startCreditAmount?: number; endCreditAmount?: number }
  ): Observable<any> {
    return this.http.put(`${this.baseUrl}/phone-logs/${phoneLogId}`, payload, this.headers);
  }
}
